import React, { useState } from 'react';
import {
  MdLanguage, MdRefresh, MdPeople, MdTravelExplore, MdSpeed, MdPhoneAndroid, MdCheckCircle, MdError,
  MdVisibility, MdVisibilityOff, MdOpenInNew,
} from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import PageShell, { Button, Notice } from '../../ui/PageShell';
import Card, { StatCard, StatGrid } from '../../ui/Card';
import { DayBars, Status } from './Services';
import './services.css';
import './sites.css';

/**
 * المواقع والتطبيقات — every public address (mtzedu.com, cp, my, the two
 * phone apps on the web, the API): is it up, who visits, from where, how
 * fast it feels, which search engines and AI crawlers come by, and who
 * actually uses which app. Data: GET /api/v1/system/sites
 * (api/src/routes/sites.js). Google PageSpeed runs on demand per site.
 */

const n = (v) => Number(v || 0).toLocaleString('en-US');
const when = (iso) => (iso
  ? new Date(iso).toLocaleString('ar-SY', { dateStyle: 'medium', timeStyle: 'short' })
  : '—');
const bytes = (b) => {
  const v = Number(b || 0);
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(2)} GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(v / 1024)} KB`;
};
const pctOf = (a, b) => (b ? Math.round((a / b) * 100) : 0);

let regionNames = null;
try { regionNames = new Intl.DisplayNames(['ar'], { type: 'region' }); } catch { /* old browser */ }
const country = (code) => {
  if (!code || code === '?' || code === 'XX' || code === 'T1') return 'غير معروف';
  try { return regionNames?.of(code) || code; } catch { return code; }
};

const BOT_CATEGORY = {
  'Search Engine Crawler': 'زاحف محرك بحث',
  'AI Crawler': 'زاحف ذكاء اصطناعي',
  'AI Assistant': 'مساعد ذكاء اصطناعي',
  'AI Search': 'بحث بالذكاء الاصطناعي',
  'Page Preview': 'معاينة رابط (واتساب، تيليغرام…)',
  'Search Engine Optimization': 'أداة تحسين محركات البحث',
  'Accessibility': 'فحص سهولة الوصول',
  'Monitoring & Analytics': 'مراقبة وإحصاء',
  'Feed Fetcher': 'قارئ خلاصات',
  'Security': 'فحص أمني',
  'Advertising & Marketing': 'إعلانات وتسويق',
};
const WHO = { admin: 'مدير', register: 'موجه', teacher: 'معلم', student: 'طالب', parent: 'ولي أمر' };
const CLIENT = { staff_app: 'تطبيق الكادر', student_app: 'تطبيق الطالب', browser: 'متصفح (ويب)' };
const DEVICE = { desktop: 'حاسوب', mobile: 'هاتف', tablet: 'لوحي' };

/** Google PageSpeed score: 90+ good, 50+ needs work, else poor. */
function Score({ v, label }) {
  if (v == null) return null;
  const tone = v >= 90 ? 'is-good' : v >= 50 ? 'is-mid' : 'is-poor';
  return <span className={`st-score ${tone}`} title={label}><b>{v}</b><small>{label}</small></span>;
}

/** Web-vitals p75 against Google's "good" thresholds. */
function Vital({ label, value, good, poor, unit = 'ms' }) {
  if (value == null) return null;
  const tone = value <= good ? 'is-good' : value <= poor ? 'is-mid' : 'is-poor';
  return <span className={`st-vital ${tone}`}>{label} <b dir="ltr">{unit === 'ms' && value >= 1000 ? `${(value / 1000).toFixed(1)} s` : `${value}${unit === 'ms' ? ' ms' : ''}`}</b></span>;
}

function MiniList({ title, rows, render, empty = 'لا بيانات' }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="st-mini">
      <h5>{title}</h5>
      {rows.length === 0 ? <p className="sv-muted">{empty}</p> : (
        <ul>
          {rows.map((r) => (
            <li key={r.key}>
              <span className="st-mini-label">{render(r)}</span>
              <span className="st-mini-bar"><span style={{ width: `${(r.count / max) * 100}%` }} /></span>
              <b>{n(r.count)}</b>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Sites() {
  const { data, loading, error, reload, setData } = useData(() => api.sitesStatus(), []);
  const [psiBusy, setPsiBusy] = useState(null);
  const [psiError, setPsiError] = useState(null);

  const vis = data?.visitors;
  const tr = data?.traffic;
  const acc = data?.accounts;
  const checks = new Map((data?.checks?.sites || []).map((c) => [c.host, c]));
  const visByHost = new Map((vis?.hosts || []).map((h) => [h.host, h]));
  const trByHost = new Map((tr?.hosts || []).map((h) => [h.host, h]));
  const psi = data?.google?.pagespeed || {};

  const totalVisits = (vis?.hosts || []).reduce((a, h) => a + h.visits, 0);
  const totalLoads = (vis?.hosts || []).reduce((a, h) => a + h.page_loads, 0);
  const totalReq = (tr?.hosts || []).reduce((a, h) => a + h.requests, 0);
  const searchCrawls = (tr?.crawlers || []).filter((c) => c.category === 'Search Engine Crawler').reduce((a, c) => a + c.count, 0);
  const activeToday = (acc?.active || []).reduce((a, r) => a + r.d1, 0);

  const runPsi = async (host) => {
    setPsiBusy(host);
    setPsiError(null);
    try {
      const r = await api.runPagespeed(host, 'mobile');
      setData((d) => ({ ...d, google: { ...d.google, pagespeed: { ...d.google.pagespeed, [`${host}:mobile`]: r } } }));
    } catch (e) {
      setPsiError({ host, code: e.code, message: e.message });
    } finally {
      setPsiBusy(null);
    }
  };

  // Active users per person type × app, for the accounts table.
  const whoRows = {};
  for (const r of acc?.active || []) {
    const row = (whoRows[r.who] ||= { who: r.who, staff_app: null, student_app: null, browser: null });
    row[r.client] = r;
  }

  return (
    <PageShell
      title="المواقع والتطبيقات"
      description={data ? `آخر تحديث: ${when(data.generated_at)}` : 'الزوار · السرعة · محركات البحث · التطبيقات'}
      actions={<Button variant="secondary" onClick={reload} loading={loading}>{!loading && <MdRefresh size={16} />} تحديث</Button>}
    >
      {error && <Notice tone="danger" title="تعذّر التحميل">{error.message}</Notice>}
      {!data && loading && <Card><p className="sv-muted">جارٍ جمع البيانات وفحص المواقع…</p></Card>}

      {data && (
        <>
          <StatGrid>
            <StatCard icon={<MdPeople />} tone="info" label="زيارات (30 يوماً)" value={vis?.ok ? n(totalVisits) : '—'} hint={vis?.ok ? `${n(totalLoads)} صفحة فُتحت` : 'Cloudflare غير مربوط'} />
            <StatCard icon={<MdLanguage />} tone="info" label="طلبات (7 أيام)" value={tr?.ok ? n(totalReq) : '—'} hint="كل العناوين معاً" />
            <StatCard icon={<MdTravelExplore />} tone="success" label="زيارات محركات البحث (7 أيام)" value={tr?.ok ? n(searchCrawls) : '—'} hint="Google وغيره" />
            <StatCard icon={<MdPhoneAndroid />} tone="success" label="مستخدمون نشطون اليوم" value={acc?.ok ? n(activeToday) : '—'} hint="من سجلات الدخول" />
          </StatGrid>

          {/* ------------------------------------------------ addresses */}
          <Card title={<span className="sv-title"><MdLanguage /> العناوين <Status s={data.checks} /></span>}>
            <div className="sv-table-wrap">
              <table className="sv-table st-sites">
                <thead>
                  <tr>
                    <th>الموقع</th><th>الحالة</th><th>زيارات 30 يوماً</th><th>طلبات 7 أيام</th>
                    <th>من الذاكرة المؤقتة</th><th>أخطاء الخادم</th><th>السرعة عند الزوار</th><th>محركات البحث</th><th>Google PageSpeed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sites.map((s) => {
                    const c = checks.get(s.host);
                    const v = visByHost.get(s.host);
                    const t = trByHost.get(s.host);
                    const vt = vis?.vitals?.[s.host];
                    const p = psi[`${s.host}:mobile`];
                    const up = c && c.status >= 200 && c.status < 400;
                    return (
                      <tr key={s.host}>
                        <td>
                          <b>{s.label}</b>
                          <a className="st-host" href={`https://${s.host}`} target="_blank" rel="noreferrer" dir="ltr">{s.host} <MdOpenInNew size={12} /></a>
                        </td>
                        <td>
                          {c ? (
                            <span className={`st-up ${up ? 'is-ok' : 'is-bad'}`}>
                              {up ? <MdCheckCircle /> : <MdError />}
                              <bdi dir="ltr">{c.status || 'لا يرد'}</bdi>
                              <small dir="ltr">{c.ms} ms</small>
                            </span>
                          ) : '—'}
                        </td>
                        <td>{v ? n(v.visits) : '—'}{v && <div className="sv-muted">{n(v.page_loads)} صفحة</div>}</td>
                        <td>{t ? n(t.requests) : '—'}{t && <div className="sv-muted" dir="ltr">{bytes(t.bytes)}</div>}</td>
                        <td>{t ? `${pctOf(t.cached, t.requests)}%` : '—'}</td>
                        <td className={t?.errors_5xx ? 'sv-bad' : ''}>{t ? n(t.errors_5xx) : '—'}</td>
                        <td>
                          {vt ? (
                            <div className="st-vitals">
                              <Vital label="أول ظهور" value={vt.fcp_ms} good={1800} poor={3000} />
                              <Vital label="اكتمال" value={vt.lcp_ms} good={2500} poor={4000} />
                              <Vital label="الاستجابة" value={vt.inp_ms} good={200} poor={500} />
                            </div>
                          ) : <span className="sv-muted">—</span>}
                        </td>
                        <td>
                          {c && s.kind !== 'api' ? (
                            c.hidden_from_search
                              ? <span className="st-seo"><MdVisibilityOff /> مخفي</span>
                              : <span className={`st-seo ${s.kind === 'private' ? 'is-warn' : 'is-ok'}`}><MdVisibility /> ظاهر</span>
                          ) : '—'}
                          {c?.sitemap && <div className="sv-muted">خريطة الموقع: {c.sitemap.status === 200 ? 'موجودة' : 'غير موجودة'}</div>}
                        </td>
                        <td>
                          {s.kind === 'api' ? '—' : (
                            <div className="st-psi">
                              {p && (
                                <div className="st-scores">
                                  <Score v={p.scores.performance} label="الأداء" />
                                  <Score v={p.scores.seo} label="SEO" />
                                  <Score v={p.scores.accessibility} label="الوصول" />
                                </div>
                              )}
                              <Button variant="secondary" loading={psiBusy === s.host} disabled={!!psiBusy} onClick={() => runPsi(s.host)}>
                                {!(psiBusy === s.host) && <MdSpeed size={14} />} {p ? 'إعادة الفحص' : 'فحص'}
                              </Button>
                              {p && <small className="sv-muted">{when(p.checked_at)}</small>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {psiError && (
              psiError.code === 'GOOGLE_SETUP' || psiError.code === 'GOOGLE_OFF' ? (
                <Notice tone="warning" title="مفتاح Google لا يسمح بفحص PageSpeed بعد">
                  في Google Cloud Console (المشروع mtzedu-9b558):
                  <ol className="st-steps">
                    <li>APIs &amp; Services ← Library ← ابحث عن <b dir="ltr">PageSpeed Insights API</b> ← Enable.</li>
                    <li>APIs &amp; Services ← Credentials ← افتح المفتاح ← API restrictions ← أضف <b dir="ltr">PageSpeed Insights API</b> ← Save.</li>
                    {psiError.code === 'GOOGLE_OFF' && <li>ثم في مجلد api: <code dir="ltr">npx wrangler secret put GOOGLE_API_KEY</code></li>}
                  </ol>
                  <small className="sv-muted" dir="ltr">{psiError.message}</small>
                </Notice>
              ) : <Notice tone="danger" title={`تعذّر فحص ${psiError.host}`}>{psiError.message}</Notice>
            )}

            {(() => {
              const p = Object.values(psi).find((x) => x.seo_issues?.length);
              return p ? (
                <Notice tone="info" title={`ملاحظات Google لتحسين الظهور في البحث (${p.host})`}>
                  <ul className="st-steps">{p.seo_issues.map((t) => <li key={t}>{t}</li>)}</ul>
                </Notice>
              ) : null;
            })()}
          </Card>

          {/* ------------------------------------------------- visitors */}
          <Card title={<span className="sv-title"><MdPeople /> الزوار — آخر 30 يوماً <Status s={vis} /></span>}>
            {!vis?.ok ? (
              <Notice tone={vis?.configured === false ? 'warning' : 'danger'} title="تعذّر قراءة الزوار">{vis?.error || 'أضف CF_API_TOKEN'}</Notice>
            ) : (
              <>
                <DayBars days={vis.days} value={(d) => d.visits} label="زيارة" />
                <div className="st-grid">
                  <MiniList title="الدول" rows={vis.countries} render={(r) => country(r.key)} />
                  <MiniList
                    title="الأجهزة"
                    rows={vis.devices}
                    render={(r) => { const [d, os] = r.key.split('|'); return `${DEVICE[d] || d} · ${os === 'Unknown' || os === '?' ? 'غير معروف' : os}`; }}
                  />
                  <MiniList title="المتصفحات" rows={vis.browsers} render={(r) => <bdi dir="ltr">{r.key}</bdi>} />
                  <MiniList
                    title="من أين جاؤوا"
                    rows={[{ key: '__direct', count: vis.direct }, ...vis.referers].filter((r) => r.count)}
                    render={(r) => (r.key === '__direct' ? 'مباشرة (رابط محفوظ أو كتابة العنوان)' : <bdi dir="ltr">{r.key}</bdi>)}
                  />
                </div>
                <h4 className="sv-sub">أكثر الصفحات فتحاً</h4>
                <div className="st-grid">
                  {data.sites.filter((s) => vis.pages[s.host]?.length).map((s) => (
                    <MiniList
                      key={s.host}
                      title={s.label}
                      rows={vis.pages[s.host].map((p) => ({ key: p.path, count: p.count }))}
                      render={(r) => <bdi dir="ltr" className="sv-code">{r.key}</bdi>}
                    />
                  ))}
                </div>
                <p className="sv-muted sv-note">أرقام الزوار من Cloudflare Web Analytics وهي تقريبية (عيّنة)، ولا تشمل الروبوتات.</p>
              </>
            )}
          </Card>

          {/* ------------------------------------- search engines / bots */}
          <Card title={<span className="sv-title"><MdTravelExplore /> محركات البحث والروبوتات — آخر 7 أيام <Status s={tr} /></span>}>
            {!tr?.ok ? (
              <Notice tone="danger" title="تعذّر القراءة">{tr?.error || 'أضف CF_API_TOKEN'}</Notice>
            ) : (
              <>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>النوع</th><th>الاسم</th><th>الطلبات</th><th>أين</th></tr></thead>
                    <tbody>
                      {tr.crawlers.map((c) => (
                        <tr key={`${c.category}|${c.name}`}>
                          <td>{BOT_CATEGORY[c.category] || c.category}</td>
                          <td dir="ltr" className="sv-code">{c.name === c.category ? '—' : c.name}</td>
                          <td>{n(c.count)}</td>
                          <td className="st-hosts">
                            {Object.entries(c.hosts).sort((a, b) => b[1] - a[1]).map(([h, k]) => (
                              <span key={h}><bdi dir="ltr">{h.replace('.mtzedu.com', '')}</bdi> {n(k)}</span>
                            ))}
                          </td>
                        </tr>
                      ))}
                      {!tr.crawlers.length && <tr><td colSpan={4} className="sv-muted">لم يزر أي روبوت معروف</td></tr>}
                    </tbody>
                  </table>
                </div>
                {tr.blocked.length > 0 && (
                  <div className="st-grid">
                    <MiniList title="طلبات مشبوهة صدّها Cloudflare (حسب الدولة)" rows={tr.blocked} render={(r) => country(r.key)} />
                  </div>
                )}
              </>
            )}
          </Card>

          {/* ------------------------------------------------- accounts */}
          <Card title={<span className="sv-title"><MdPhoneAndroid /> من يستخدم ماذا <Status s={acc} /></span>}>
            {!acc?.ok ? (
              <Notice tone="danger" title="تعذّر القراءة">{acc?.error}</Notice>
            ) : (
              <>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead>
                      <tr>
                        <th>الفئة</th>
                        {Object.values(CLIENT).map((c) => <th key={c}>{c}<div className="sv-muted">اليوم · 7 أيام · 30 يوماً</div></th>)}
                        <th>كل الحسابات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['admin', 'register', 'teacher', 'student', 'parent'].map((w) => {
                        const row = whoRows[w] || {};
                        const total = (acc.accounts || []).find((a) => a.who === w);
                        return (
                          <tr key={w}>
                            <td><b>{WHO[w]}</b></td>
                            {Object.keys(CLIENT).map((c) => (
                              <td key={c}>{row[c] ? <><b>{n(row[c].d1)}</b> · {n(row[c].d7)} · {n(row[c].d30)}</> : <span className="sv-muted">—</span>}</td>
                            ))}
                            <td>{total ? n(total.n) : 0}{total?.locked ? <span className="sv-muted"> (موقوف {n(total.locked)})</span> : null}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="sv-facts st-facts">
                  {Object.entries(CLIENT).map(([k, label]) => (
                    <div key={k}>
                      <small>جلسات مفتوحة — {label}</small>
                      <b>{n((acc.open_sessions || []).find((s) => s.client === k)?.open_sessions)}</b>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </PageShell>
  );
}
